// Module 19 (BL-380) G1 - the Events board's reads and writes. Every call runs under the signed-in user's own RLS.
import { DEFAULT_TENANT_ZONE, rsvpSummaryMap, validZone } from './eventsBoard';

// The event's status and brand live on engagements (a Dispatch-owned table an Events-only planner cannot read), so they come
// through the checked m19_event_statuses() function instead of an embedded read.
const EVENT_COLUMNS_BEFORE_289 = 'id, engagement_id, event_name, event_date, start_time, end_time, venue_name, venue_address, event_type, expected_guest_count, notes';
const EVENT_COLUMNS = `${EVENT_COLUMNS_BEFORE_289}, end_date, event_type_other`;

function blank(value) {
    const text = String(value ?? '').trim();
    return text === '' ? null : text;
}

function rpcArgs(form) {
    const guests = String(form.expected_guest_count ?? '').trim();
    return {
        p_event_name: String(form.event_name || '').trim(),
        p_event_date: form.event_date,
        p_start_time: blank(form.start_time),
        p_end_time: blank(form.end_time),
        p_venue_name: blank(form.venue_name),
        p_venue_address: blank(form.venue_address),
        p_event_type: blank(form.event_type),
        p_expected_guest_count: guests === '' ? null : Number(guests),
        p_notes: blank(form.notes),
    };
}

// Migration 289: the database also checks that the end is after the start and that an "Other" event has its name.
function fullValues(form) {
    const args = rpcArgs(form);
    return {
        event_name: args.p_event_name, event_date: args.p_event_date, end_date: blank(form.end_date), start_time: args.p_start_time, end_time: args.p_end_time,
        venue_name: args.p_venue_name, venue_address: args.p_venue_address, event_type: args.p_event_type, event_type_other: blank(form.event_type_other),
        expected_guest_count: args.p_expected_guest_count, notes: args.p_notes,
        // New events only: on an existing event saveEventExtras saves these, and the update function ignores them.
        plus_ones_enabled: Boolean(form.plus_ones_enabled), plus_ones_max: form.plus_ones_enabled ? form.plus_ones_max : 0,
        guest_transport_mode: form.guest_transport_mode || 'client_arranged', transport_provider_name: blank(form.transport_provider_name),
        coordinator_name: blank(form.coordinator_name), coordinator_phone: blank(form.coordinator_phone), coordinator_email: blank(form.coordinator_email),
        guest_visibility: form.guest_visibility || {},
    };
}

// { engagement_id -> { id, status, entity_id } } for the events the signed-in user may see.
export async function loadEventStatuses(client, engagementId = null) {
    const { data, error } = await client.rpc('m19_event_statuses', engagementId ? { p_engagement_id: engagementId } : {});
    if (error) throw error;
    return new Map((data || []).map((row) => [row.engagement_id, { id: row.engagement_id, status: row.status, entity_id: row.entity_id }]));
}

export async function loadEvents(client) {
    const read = (columns) => client
        .from('event_engagement_details')
        .select(columns)
        .order('event_date', { ascending: true })
        .order('start_time', { ascending: true, nullsFirst: true });
    let { data, error } = await read(EVENT_COLUMNS);
    // Code live minutes before migration 289 is applied: read the columns that exist.
    if (error && /42703|end_date|event_type_other/.test(`${error.code || ''} ${error.message || ''}`)) ({ data, error } = await read(EVENT_COLUMNS_BEFORE_289));
    if (error) throw error;
    const statuses = await loadEventStatuses(client);
    return (data || []).map((row) => ({ ...row, engagement: statuses.get(row.engagement_id) || null }));
}

// DC-1: every event's RSVP numbers in ONE read (invited hosts, yes, no, maybe, no reply; plus-ones are not invitations - Q-031), summed
// in the database under the caller's own row security (migration 230). Events with no guests have no row.
export async function loadEventRsvpSummary(client) {
    const { data, error } = await client.rpc('m19_event_rsvp_summary');
    if (error) throw error;
    return rsvpSummaryMap(data);
}

// The company time zone, for "today" and the 30-day window. Falls back to the database's own default if it cannot be read.
export async function loadTenantTimeZone(client, orgId) {
    if (!orgId) return DEFAULT_TENANT_ZONE;
    try {
        const { data } = await client.rpc('org_time_zone', { p_org_id: orgId });
        return validZone(data) || DEFAULT_TENANT_ZONE;
    } catch {
        return DEFAULT_TENANT_ZONE;
    }
}

// The brands an event can be created under: the organization's Events entities the user may plan for (a checked read).
export async function loadEventEntities(client) {
    const { data, error } = await client.rpc('m19_event_entities');
    if (error) throw error;
    return data || [];
}

// Seeds the organization's four starter kits the first time (idempotent), then reads them.
export async function loadEventKits(client) {
    try {
        await client.rpc('ensure_event_type_kits');
    } catch {
        // Seeding is a convenience; reading below still shows whatever kits exist.
    }
    const { data, error } = await client
        .from('event_type_kits')
        .select('id, key, label, description, default_checklist, sort_order, is_active')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
    if (error) throw error;
    return data || [];
}

// The event's single row, for the Event page's Edit-event modal.
export async function loadEventRow(client, engagementId) {
    const { data, error } = await client.from('event_engagement_details').select(EVENT_COLUMNS).eq('engagement_id', engagementId).maybeSingle();
    if (error) throw error;
    return data;
}

// What the Edit-event modal adds beyond the event's own columns: invitation settings once they have loaded, and any pick from the approved list.
export function placesFromForm(form) {
    return {
        venue: { vendor_id: form.venue_vendor_id || null, name: form.venue_name || '', address: form.venue_address || '' },
        transport_provider: { vendor_id: form.provider_vendor_id || null, name: form.transport_provider_name || '' },
        hotels: (form.hotels || []).filter((h) => String(h.name || '').trim()).map((h) => ({ vendor_id: h.vendor_id || null, name: h.name, address: h.address || '' })),
    };
}
export async function saveEventExtras(client, engagementId, form) {
    if (form.guest_settings_loaded) {
        const { error } = await client.rpc('m19_save_event_guest_options', { p_event: engagementId, p_values: { plus_ones_enabled: Boolean(form.plus_ones_enabled), plus_ones_max: form.plus_ones_enabled ? form.plus_ones_max : 0,
            guest_transport_mode: form.guest_transport_mode || 'client_arranged', transport_provider_name: form.transport_provider_name || '', coordinator_name: form.coordinator_name || '', coordinator_phone: form.coordinator_phone || '', coordinator_email: form.coordinator_email || '', guest_visibility: form.guest_visibility || {} } });
        if (error) throw error;
    }
    if (form.places_changed) {
        const { error } = await client.rpc('m19_save_event_places', { p_event: engagementId, p_places: placesFromForm(form) });
        if (error) throw error;
    }
}

export async function createEvent(client, form) {
    const { data, error } = await client.rpc('m19_create_event_full', { p_entity_id: form.entity_id, p_values: fullValues(form) });
    if (error) throw error;
    return data;
}

export async function updateEvent(client, engagementId, form) {
    const { error } = await client.rpc('m19_update_event_full', { p_engagement_id: engagementId, p_values: fullValues(form) });
    if (error) throw error;
}

// Cancel is the only status change an event allows for now (the database refuses the rest).
export async function cancelEvent(client, engagementId) {
    const { error } = await client.rpc('m19_cancel_event', { p_engagement_id: engagementId });
    if (error) throw error;
}
